import React from 'react';
import { Box, Text } from '@primer/react';
import { DownloadIcon, LinkExternalIcon, MarkGithubIcon } from '@primer/octicons-react';

const Introduction = () => {

    const handleDownload = () => {
        // Create a link element
        const link = document.createElement('a');

        // Set the href to the PDF file in the public folder
        link.href = '/dylan_cattelan_resume.pdf';

        // Set the download attribute to suggest a filename
        link.download = 'dylan_cattelan_resume.pdf';

        // Append to the body, click programmatically, then remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box mb={6}>
            <Box mt={65}>
                <Text
                    as="p"
                    sx={{
                        fontSize: [2, 3],
                        lineHeight: 1.5,
                        color: 'fg.muted',
                        mb: 4
                    }}
                >
                    My journey into software engineering began at 13 when I discovered programming through a French educational platform called <b>'Le Site du Zéro'</b>. That first CLI McDonald's menu selection program in C sparked a passion that has driven my career ever since. The ability to create interactive solutions through code was immediately captivating and set the foundation for my technical expertise.
                </Text>

                <Text
                    as="p"
                    sx={{
                        fontSize: [2, 3],
                        lineHeight: 1.5,
                        color: 'fg.muted',
                        mb: 4
                    }}
                >
                    After completing a literature-focused high school diploma, I initially pursued English and Japanese languages at Université Toulouse Jean Jaurès. However, I soon realized my true calling lay in technology and engineering. This led me to <b>CESI Engineering School</b> in France, where I earned my engineering degree as a <b>Valedictorian</b> during 3 out of 5 years.
                </Text>

                <Text
                    as="p"
                    sx={{
                        fontSize: [2, 3],
                        lineHeight: 1.5,
                        color: 'fg.muted',
                        mb: 4
                    }}
                >
                    Throughout my academic career, I gained hands-on experience through various internships and positions. I built health monitoring dashboards at <b>CNRS (France's National Scientific Research Center)</b>, developed database systems for healthcare companies, and completed an international internship in <b>London</b> focusing on web development. I also explored data science at <b>Infotel</b>, where I developed predictive analytics solutions, though I discovered my passion lay more in building robust systems than data analysis.
                </Text>

                <Text
                    as="p"
                    sx={{
                        fontSize: [2, 3],
                        lineHeight: 1.5,
                        color: 'fg.muted',
                        mb: 4
                    }}
                >
                    My professional career took off at <b>Teazit</b> in Lyon, where I engineered a comprehensive VOD management system and built multi-platform live streaming services for video professionals. The role involved Python/Django backend development and Vue.js frontend integration, focusing primarily on backend architecture and optimization.
                </Text>

                <Text
                    as="p"
                    sx={{
                        fontSize: [2, 3],
                        lineHeight: 1.5,
                        color: 'fg.muted',
                        mb: 4
                    }}
                >
                    I spent 3 years as a Software Engineer at Fieldwire by Hilti in <b>San Francisco</b>, working remotely from France. I progressed from L2 to L3 level, leading the development of integration systems connecting Fieldwire with Google Drive and SharePoint, and architecting high-performance document processing pipelines capable of handling 10,000,000+ files for enterprise clients.
                </Text>

                <Text
                    as="p"
                    sx={{
                        fontSize: [2, 3],
                        lineHeight: 1.5,
                        color: 'fg.muted',
                        mb: 4
                    }}
                >
                    Most recently, I have joined <b>Dialogue</b>, a healthcare company, as a Senior Software Engineer, part of the Platform team. Here, I focus on building scalable backend services and integrations that support Dialogue's telemedicine platform, ensuring reliable and efficient healthcare delivery to our users.
                </Text>

                <Text
                    as="p"
                    sx={{
                        fontSize: [2, 3],
                        lineHeight: 1.5,
                        color: 'fg.muted',
                        mb: 5
                    }}
                >
                    With over 6 years of professional development experience, I specialize in backend systems, distributed architectures, and building resilient integrations. Currently based in <b>Montreal, Canada</b>, I'm passionate about solving complex technical challenges and contributing to meaningful projects that make a real impact.
                </Text>
            </Box>

            <Box mt={65}>
                <Text
                    as="p"
                    sx={{
                        fontSize: [2, 3],
                        lineHeight: 1.5,
                        color: 'fg.muted',
                        mb: 5
                    }}
                >
                    Interested in learning more about my experience and technical expertise? Here are some resources to explore my professional background:
                </Text>
            </Box>

            {/* Resources section */}
            <Box
                display="flex"
                flexDirection={["column", "row"]}
                mb={4}
                gap={6}
                mt={5}
            >
                <Box
                    as="button"
                    onClick={handleDownload}
                    bg="success.subtle"
                    px={3}
                    py={2}
                    mr={[0, 3]}
                    mb={[2, 0]}
                    sx={{
                        border: '1px solid',
                        borderColor: 'success.muted',
                        borderRadius: 2,
                        fontWeight: 700,
                        cursor: 'pointer',
                        color: 'success.fg',
                        textDecoration: 'none',
                        '&:hover': {
                            bg: 'success.subtle',
                            borderColor: 'success.muted',
                            textDecoration: 'none'
                        }
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <Box mr={2}><DownloadIcon size={16} /></Box>
                        <Text>Download Resume</Text>
                    </Box>
                </Box>

                <Box
                    as="a"
                    href="https://linkedin.com/in/dylancattelan"
                    target="_blank"
                    bg="accent.subtle"
                    px={3}
                    py={2}
                    mr={[0, 3]}
                    mb={[2, 0]}
                    sx={{
                        border: '1px solid',
                        borderColor: 'accent.muted',
                        borderRadius: 2,
                        fontWeight: 500,
                        cursor: 'pointer',
                        color: 'accent.fg',
                        textDecoration: 'none',
                        '&:hover': {
                            bg: 'accent.subtle',
                            borderColor: 'accent.muted',
                            textDecoration: 'none'
                        }
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <Box mr={2}><LinkExternalIcon size={16} /></Box>
                        <Text>LinkedIn Profile</Text>
                    </Box>
                </Box>

                <Box
                    as="a"
                    href="https://github.com/dylanca"
                    target="_blank"
                    bg="attention.subtle"
                    px={3}
                    py={2}
                    sx={{
                        border: '1px solid',
                        borderColor: 'attention.muted',
                        borderRadius: 2,
                        fontWeight: 500,
                        cursor: 'pointer',
                        color: 'attention.fg',
                        textDecoration: 'none',
                        '&:hover': {
                            bg: 'attention.subtle',
                            borderColor: 'attention.muted',
                            textDecoration: 'none'
                        }
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <Box mr={2}><MarkGithubIcon size={16} /></Box>
                        <Text>GitHub Profile</Text>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Introduction;